import { matchSorter } from 'match-sorter';
import type { StaffStatCatalogRow } from '~/hooks/queries/useStaffStatColumnCatalogQuery';
import {
  groupByPrimaryTag,
  sortTagHeaders,
} from '~/views/Dashboard/AnalyticsStats/analyticsStatsColumnListUtils';

export type DimensionBundle = {
  dimension: StaffStatCatalogRow;
  attributes: StaffStatCatalogRow[];
};

function groupingKey(row: StaffStatCatalogRow): string | null {
  if (row.kind !== 'grouping' || row.columnRef.kind !== 'grouping') return null;
  return row.columnRef.key;
}

/** Dimensions with nested display rows whose `idColumnAlias` matches the grouping key. */
export function buildDimensionBundles(catalog: StaffStatCatalogRow[]): DimensionBundle[] {
  const groupingRows = catalog
    .filter((r) => r.kind === 'grouping')
    .sort((a, b) => a.label.localeCompare(b.label));
  const keys = new Set(
    groupingRows.map((r) => groupingKey(r)).filter((k): k is string => k != null),
  );
  const displayRows = catalog.filter((r) => r.kind === 'display');

  return groupingRows.map((dimension) => {
    const key = groupingKey(dimension);
    const attributes =
      key != null && keys.has(key)
        ? displayRows
            .filter((d) => d.idColumnAlias === key)
            .sort((a, b) => a.label.localeCompare(b.label))
        : [];
    return { dimension, attributes };
  });
}

/** Display catalog rows not tied to a known grouping dimension in this catalog. */
export function standaloneDisplayRows(catalog: StaffStatCatalogRow[]): StaffStatCatalogRow[] {
  const keys = new Set(
    catalog
      .filter((r) => r.kind === 'grouping')
      .map((r) => groupingKey(r))
      .filter((k): k is string => k != null),
  );
  return catalog
    .filter((r) => {
      if (r.kind !== 'display') return false;
      if (!r.idColumnAlias || !keys.has(r.idColumnAlias)) return true;
      return false;
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function statRowsInCatalog(catalog: StaffStatCatalogRow[]): StaffStatCatalogRow[] {
  return catalog.filter((r) => r.kind === 'stat').sort((a, b) => a.label.localeCompare(b.label));
}

export type CatalogTagItem = {
  id: string;
  tags: string[];
  label: string;
  tooltip?: string;
  row: StaffStatCatalogRow;
};

export function groupCatalogRowsByPrimaryTag(
  rows: StaffStatCatalogRow[],
  tagSortOrder?: string[],
): { groups: Map<string, CatalogTagItem[]>; headers: string[]; untagged: StaffStatCatalogRow[] } {
  const withTags = rows.filter((r) => (r.tags?.length ?? 0) > 0);
  const untagged = rows.filter((r) => !(r.tags?.length ?? 0));
  const items: CatalogTagItem[] = withTags.map((row) => ({
    id: row.catalogId,
    tags: row.tags ?? [],
    label: row.label,
    tooltip: row.tooltip,
    row,
  }));
  const groups = groupByPrimaryTag(items);
  const headers = sortTagHeaders([...groups.keys()], tagSortOrder);
  return { groups, headers, untagged };
}

/** Tag quick actions include display rows plus their parent dimension row when present. */
export function collectCatalogIdsByTagWithParentDimensions(
  catalog: StaffStatCatalogRow[],
): Map<string, string[]> {
  const idsByTag = new Map<string, Set<string>>();
  const groupingCatalogIdByKey = new Map<string, string>();

  catalog.forEach((row) => {
    const key = groupingKey(row);
    if (key) {
      groupingCatalogIdByKey.set(key, row.catalogId);
    }
  });

  catalog.forEach((row) => {
    row.tags?.forEach((tag) => {
      const normalisedTag = tag.trim();
      if (!normalisedTag) return;

      const ids = idsByTag.get(normalisedTag) ?? new Set<string>();
      ids.add(row.catalogId);

      if (row.kind === 'display' && row.idColumnAlias) {
        const parentDimensionId = groupingCatalogIdByKey.get(row.idColumnAlias);
        if (parentDimensionId) {
          ids.add(parentDimensionId);
        }
      }

      idsByTag.set(normalisedTag, ids);
    });
  });

  return new Map([...idsByTag].map(([tag, ids]) => [tag, [...ids]]));
}

/**
 * When searching: keep rows that match labels or primary tag headers. Keep whole dimension families
 * for label matches, and keep parent dimensions visible for nested tag matches.
 */
export function filterCatalogForStaffSearch(
  catalog: StaffStatCatalogRow[],
  query: string,
): StaffStatCatalogRow[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...catalog];

  const labelMatches = new Set(
    matchSorter(catalog, q, {
      keys: ['label'],
    }).map((row) => row.catalogId),
  );
  const primaryTagMatches = new Set(
    matchSorter(catalog, q, {
      keys: [(row) => row.tags?.[0] ?? ''],
    }).map((row) => row.catalogId),
  );
  const rowMatches = (row: StaffStatCatalogRow) =>
    labelMatches.has(row.catalogId) || primaryTagMatches.has(row.catalogId);

  const familyKeysMatchingByLabel = new Set<string>();
  const parentKeysNeededForTaggedDisplays = new Set<string>();
  for (const r of catalog) {
    if (r.kind === 'grouping' && labelMatches.has(r.catalogId)) {
      const key = groupingKey(r);
      if (key) familyKeysMatchingByLabel.add(key);
    }
    if (r.kind === 'display' && labelMatches.has(r.catalogId) && r.idColumnAlias) {
      familyKeysMatchingByLabel.add(r.idColumnAlias);
    }
    if (r.kind === 'display' && primaryTagMatches.has(r.catalogId) && r.idColumnAlias) {
      parentKeysNeededForTaggedDisplays.add(r.idColumnAlias);
    }
  }

  return catalog.filter((row) => {
    if (rowMatches(row)) return true;
    const gk = groupingKey(row);
    if (
      row.kind === 'grouping' &&
      gk &&
      (familyKeysMatchingByLabel.has(gk) || parentKeysNeededForTaggedDisplays.has(gk))
    ) {
      return true;
    }
    if (
      row.kind === 'display' &&
      row.idColumnAlias &&
      familyKeysMatchingByLabel.has(row.idColumnAlias)
    ) {
      return true;
    }
    return false;
  });
}

export function collectCatalogIdsFromDimensionBundles(bundles: DimensionBundle[]): string[] {
  const ids: string[] = [];
  for (const b of bundles) {
    ids.push(b.dimension.catalogId);
    for (const a of b.attributes) {
      ids.push(a.catalogId);
    }
  }
  return ids;
}

/** Minimal catalog row shape for column-config sort order (account + global catalogs). */
export type StaffCatalogSortableRow = {
  catalogId: string;
  label: string;
  kind: 'grouping' | 'stat' | 'display';
  ref: string;
  tags?: string[] | null;
  idColumnAlias?: string | null;
  columnRef?: StaffStatCatalogRow['columnRef'];
};

function sortableGroupingKey(row: StaffCatalogSortableRow): string | null {
  if (row.kind !== 'grouping') return null;
  if (row.columnRef?.kind === 'grouping') return row.columnRef.key;
  return row.ref;
}

function buildSortableDimensionBundles(catalog: StaffCatalogSortableRow[]) {
  const groupingRows = catalog
    .filter((r) => r.kind === 'grouping')
    .sort((a, b) => a.label.localeCompare(b.label));
  const keys = new Set(
    groupingRows.map((r) => sortableGroupingKey(r)).filter((k): k is string => k != null),
  );
  const displayRows = catalog.filter((r) => r.kind === 'display');

  return groupingRows.map((dimension) => {
    const key = sortableGroupingKey(dimension);
    const attributes =
      key != null && keys.has(key)
        ? displayRows
            .filter((d) => d.idColumnAlias === key)
            .sort((a, b) => a.label.localeCompare(b.label))
        : [];
    return { dimension, attributes };
  });
}

function sortableStandaloneDisplayRows(
  catalog: StaffCatalogSortableRow[],
): StaffCatalogSortableRow[] {
  const keys = new Set(
    catalog
      .filter((r) => r.kind === 'grouping')
      .map((r) => sortableGroupingKey(r))
      .filter((k): k is string => k != null),
  );
  return catalog
    .filter((r) => {
      if (r.kind !== 'display') return false;
      if (!r.idColumnAlias || !keys.has(r.idColumnAlias)) return true;
      return false;
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

function sortableStatRows(catalog: StaffCatalogSortableRow[]): StaffCatalogSortableRow[] {
  return catalog.filter((r) => r.kind === 'stat').sort((a, b) => a.label.localeCompare(b.label));
}

function appendRowsGroupedByPrimaryTag<T extends StaffCatalogSortableRow>(
  ordered: T[],
  rows: T[],
  tagSortOrder?: string[],
): void {
  const withTags = rows.filter((r) => (r.tags?.length ?? 0) > 0);
  const untagged = rows.filter((r) => !(r.tags?.length ?? 0));
  const items = withTags.map((row) => ({
    id: row.catalogId,
    tags: row.tags ?? [],
    label: row.label,
    row,
  }));
  const groups = groupByPrimaryTag(items);
  const headers = sortTagHeaders([...groups.keys()], tagSortOrder);
  for (const header of headers) {
    for (const item of groups.get(header) ?? []) {
      ordered.push(item.row);
    }
  }
  for (const row of untagged) {
    ordered.push(row);
  }
}

/**
 * Flat sort order matching StatConfig / StaffUnifiedColumnCatalogPage:
 * dimensions (with nested attributes by tag), metrics by tag, then standalone details by tag.
 */
export function sortStaffCatalogRowsForColumnConfig<T extends StaffCatalogSortableRow>(
  catalog: T[],
  options?: {
    statColumnTagsSortOrder?: string[];
    displayColumnTagsSortOrder?: string[];
  },
): T[] {
  const ordered: T[] = [];

  for (const { dimension, attributes } of buildSortableDimensionBundles(catalog)) {
    ordered.push(dimension as T);
    appendRowsGroupedByPrimaryTag(ordered, attributes as T[], options?.displayColumnTagsSortOrder);
  }

  appendRowsGroupedByPrimaryTag(
    ordered,
    sortableStatRows(catalog) as T[],
    options?.statColumnTagsSortOrder,
  );

  appendRowsGroupedByPrimaryTag(
    ordered,
    sortableStandaloneDisplayRows(catalog) as T[],
    options?.displayColumnTagsSortOrder,
  );

  return ordered;
}
